import { useTranslation } from 'react-i18next';

import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TerminalIcon from '@mui/icons-material/Terminal';

import { useHostingSitesV2 } from '@newfold/huapi-js';

import wordpressCloneIcon from '~/assets/images/wordpress-clone-icon.svg';
import wordpressIcon from '~/assets/images/wordpress-icon.svg';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';

export interface SiteOption {
  id: number;
  title: string;
  description: string | JSX.Element;
  testId: string;
  subtype: 'wordpress' | 'generic' | 'MigrateSite';
  cloneWordpress?: boolean;
  enabled?: boolean;
  action?: 'add' | 'clone' | 'transfer';
  navigateTo?: string;
  cardLogo?: string;
  cardIcon?: JSX.Element;
}

export default function useSiteOptions(
  isCloudHosting: boolean,
): SiteOption[] | null {
  const { t } = useTranslation('addSites', { keyPrefix: 'addSiteMenuOptions' });
  const { localizedBrandName } = useBrandInfo();
  const { id: hostingId } = useAccount();

  const { data: sitesData } = useHostingSitesV2(
    hostingId,
    {},
    {
      query: {
        enabled: !!hostingId,
        select: (data) => ({ data }),
      },
    },
  );

  const canPerformClone = (sitesData?.data?.data?.total ?? 0) > 0;

  if (isCloudHosting) {
    return [
      {
        id: 1,
        title: t('cloud.wordpressOption'),
        description: t('cloud.wordpressDesc'),
        testId: 'AddSiteMenuOption-WordPress',
        subtype: 'wordpress',
        cardLogo: wordpressIcon,
        cloneWordpress: false,
        enabled: true,
        action: 'add',
        navigateTo: 'title',
      },
      {
        id: 2,
        title: t('cloud.cloneSiteOption'),
        description: t('cloud.cloneSiteDesc'),
        subtype: 'wordpress',
        cardLogo: wordpressCloneIcon,
        testId: 'AddSiteMenuOption-CloneSite',
        cloneWordpress: true,
        enabled: canPerformClone,
        action: 'clone',
        navigateTo: 'site',
      },
      {
        id: 3,
        title: t('cloud.transferSiteTitle'),
        description: t('cloud.transferSiteDesc'),
        subtype: 'wordpress',
        testId: 'AddSiteMenuOption-TransferSite',
        cardIcon: <TerminalIcon sx={{ fontSize: 40 }} />,
        cloneWordpress: false,
        enabled: true,
        action: 'transfer',
        navigateTo: 'config',
      },
    ];
  }

  return [
    {
      id: 1,
      title: t('wordpressOption'),
      description: t('wordpressDesc'),
      testId: 'AddSiteMenuOption-WordPress',
      subtype: 'wordpress',
      cardLogo: wordpressIcon,
      enabled: true,
    },
    {
      id: 2,
      title: t('migrateSiteOption'),
      description: t('migrateSiteOptionDesc', { brand: localizedBrandName }),
      testId: 'AddSiteMenuOption-MigrateSite',
      subtype: 'MigrateSite',
      cardIcon: <CompareArrowsIcon sx={{ fontSize: 40 }} />,
      enabled: true,
    },
    {
      id: 3,
      title: t('emptyEnvironmentOption'),
      description: t('emptyEnvironmentDesc'),
      subtype: 'generic',
      cardIcon: <TerminalIcon sx={{ fontSize: 40 }} />,
      testId: 'AddSiteMenuOption-Generic',
      enabled: true,
    },
  ];
}
